// src/services/aeo/deliveryTypes.ts
//
// Delivery: what staff owe each client, deliverable by deliverable. The wire
// shapes the backend's Delivery routes return (handoffs H4, H5, H7, H14, H16,
// H22, H23, H28), and the sample store serves the same shapes until they ship.
//
// Four levels, one per screen: the deliverable (code x plan month, S2 and S4
// per outlet) is what the client sees; its next step is what the staff list
// shows; its items sit in the drawer; a check's pages sit inside its item.
//
// Oura CHECKS the work and people DO it. No field here makes Oura an actor:
// an item waiting on Oura is waiting on a re-check of work a person did.
//
// Contract v1.3 (2 Oct 2026, with the Docs write contract v1.0): the
// `create_doc` main action and the deliverable's `docStep` (H31), the publish
// refusals and a staff override with a reason (H20a), and the Phase 3 fields
// on items (H32 to H39): drafts a person started, the paste list, a proof
// file, the "Live since" reply drafted for a person, the AI reader checks and
// approved copy to the site. All optional: absent = the server does not send
// it yet.

/** check and record read "Oura checks it"; page reads "A page goes live"; confirm reads "We confirm it". */
export type ItemKind = "check" | "record" | "page" | "confirm";

export type ItemState = "todo" | "waiting" | "done";

/** Whom a Waiting item waits on: Oura's re-check, or the client. */
export type WaitingOn = "oura" | "client";

/** A failing page of a check, or the page a slot is filled with. */
export interface ItemPage {
    /** A path ("/menu") or an address. */
    url: string;
    state: "todo" | "waiting" | "done" | "not_read" | "closed_by_hand" | "no_action";
    /** "5 of 9 images", "4.1 s on a phone", "2 hops". */
    detail?: string | null;
    markedFixedAt?: string | null;
    cameBack?: boolean;
    noActionReason?: string | null;
}

export type ProofKind = "scan" | "run" | "connection" | "report" | "reading" | "publish" | "live" | "link" | "note" | "by_hand";

export interface Proof {
    kind: ProofKind;
    /** "12 Oct scan", "Profile link", "Note by Dana". */
    text: string;
    at?: string | null;
    url?: string | null;
    by?: string | null;
}

export interface ItemSlot {
    /** 1-based, within the deliverable. */
    index: number;
    topic?: string | null;
    /** Agreed with the client during the work; null until then. */
    pageKind?: "new" | "existing" | null;
    signature?: boolean;
    /** What changed on an existing page, as staff wrote it. */
    changed?: string | null;
}

export interface DeliveryItem {
    /** Stable: `${deliverableKey}:${part}`. */
    id: string;
    kind: ItemKind;
    /** The audit check this item tracks, for kind "check". */
    checkKey?: string | null;
    /** Plain words: "Summary lines". */
    label: string;
    /** The check's technical name, shown small: "Missing meta description". */
    technical?: string | null;
    scope?: "key_pages" | "whole_site" | "site" | null;
    state: ItemState;
    waitingOn?: WaitingOn | null;
    cameBack: boolean;
    due?: string | null;
    pages: ItemPage[];
    /** A check's balance: failing = fixed + waiting on scan + to do. */
    counts?: { failing: number; fixed: number; waitingOnScan: number; todo: number } | null;
    outlet?: string | null;
    slot?: ItemSlot | null;
    proof?: Proof | null;
    markedFixedAt?: string | null;
    /** Who marked it fixed (Mark fixed, or the last paste step). Proposed BE field; absent = not named. */
    markedFixedBy?: string | null;
    closedByHand?: boolean;
    /**
     * Staff marked it Done by hand (#6463): one click on the Checklist, for work that is finished
     * but whose trigger never fired. Sticky until Undo (untick). `whole` is true when the whole
     * deliverable was marked, and then its Undo is the deliverable's. Absent on an older server.
     */
    forcedDone?: { by: string; at: string; whole: boolean } | null;
    /** Waiting on the client since. */
    askedClientAt?: string | null;
    /** The check does not apply to this site: "No practitioner pages". Counts as Done. */
    doesNotApply?: string | null;
    note?: string | null;
    /** Staff do: "Write them, then Mark fixed". */
    staffDo?: string | null;
    /**
     * For a record: the staff view its work happens on ("reviews",
     * "questions", "connectors", "dashboard", "setup"). The server sends it;
     * without it the record's name in the id decides.
     */
    goTo?: string | null;
    /** The next step this item gives, by template: "Describe 14 images on 5 key pages". */
    nextText?: string | null;
    /** What we asked the client, in the client's words: "Send us the hours and phone for Bukit Bintang". */
    clientAsk?: string | null;
    /** The client answered in the document and the reply waits on us. */
    clientRepliedAt?: string | null;
    /**
     * When Oura's scan last read this check inside the deliverable's window.
     * A check with no failing pages is clear only once it has been read; until
     * then it waits on Oura's scan (never an invented clear).
     */
    readAt?: string | null;
    /**
     * H32 (v1.3): the drafts a PERSON started for this item, in the Pages
     * workshop or Site issues, and where each stands. The scan's own page
     * drafts never show here. [] = none started.
     */
    drafts?: StartedDraft[];
    /** H35 (v1.3): the paste list, for a change Oura cannot publish on this site. */
    pasteList?: PasteList | null;
    /** H36 (v1.3): the file staff attached when they closed it by hand. */
    proofFile?: ProofFile | null;
    /** H37 (v1.3): the "Live since" reply, drafted for a person to post in the doc. Never posted by Oura. */
    liveSinceDraft?: LiveSinceDraft | null;
    /** H38 (v1.3): the AI reader test, 1 line per reader (A3's AI readers item). null = not run yet. */
    readerChecks?: ReaderCheck[] | null;
    /** H39 (v1.3): approved copy to the site: an unpublished draft page where the site allows it, else a download. */
    copyToSite?: CopyToSite | null;
    /**
     * Added for the usability round (2 Oct 2026, additive): what a block
     * meant for the plan, in one line ("ClaudeBot was blocked from 3 Sep to
     * 1 Oct, so Claude's answers could not read the site"). Absent = none.
     */
    impact?: string | null;
}

/* ------------------------------------------------------------------------- *
 * Phase 3 on items (contract v1.3, handoffs H32 to H39)
 * ------------------------------------------------------------------------- */

/**
 * A draft a person started from an item: a page draft in the Pages workshop
 * or a fix draft in Site issues. `published` is a named person's click on the
 * client's site (one change at a time); `pasted` is a paste list ticked done.
 */
export interface StartedDraft {
    id: string;
    kind: "page" | "fix";
    /** The page it changes: "/menu". */
    page: string;
    /** "Summary line for /menu". */
    label: string;
    state: "draft" | "published" | "pasted";
    /** The named person who started it. Never Oura. */
    startedBy: string;
    startedAt: string;
    /** The doc question whose approved answer it was made from (3.2); null = none. */
    fromQuestionId?: string | null;
    /** The doc that question is in; null = none. */
    fromDocId?: string | null;
    /** Lane 6 (v1.3, additive): that question's number in its doc, for "From the client's answer to Q3"; null = not numbered. */
    fromQuestionNumber?: number | null;
    publishedAt?: string | null;
    publishedBy?: string | null;
    /** The H20 key the publish gate reads: "S1:missing_meta", or a slot id. */
    approvalKey?: string | null;
}

/** Where a paste list's steps happen. */
export type SitePlatform = "wix" | "wordpress" | "framer" | "webflow" | "squarespace" | "other";

/** H35: a change staff (or the client's developer) make by hand, in exact words. */
export interface PasteList {
    platform: SitePlatform;
    steps: PasteStep[];
    /** Ticking every step marks the item fixed, so Oura's same-day re-check reads it. */
    allTicked: boolean;
    /** The list as plain text for the client's developer (H35's export). */
    forDeveloper: string;
}
export interface PasteStep {
    id: string;
    /** The click path: "Pages, then Menu, then SEO basics, then Meta description". */
    where: string;
    /** The exact words to paste; null for a step with nothing to paste. */
    words: string | null;
    ticked: boolean;
    tickedBy?: string | null;
    tickedAt?: string | null;
    /**
     * Added for the usability round (2 Oct 2026, additive): the page this
     * step changes, so its tick moves that page's row. Absent = the step
     * changes no one page.
     */
    page?: string | null;
}

/** H36: proof attached on close-by-hand. */
export interface ProofFile {
    name: string;
    url: string;
    addedBy: string;
    addedAt: string;
}

/** H37: the reply a person posts in the doc once the change is live. Drafted, never posted by Oura. */
export interface LiveSinceDraft {
    /** "Live since Thu 1 Oct: the summary line on /menu now reads ..." */
    text: string;
    docId: string;
    threadId: string;
    madeAt: string;
    /** Set when a named person posted it (from Oura's reply box, B20); null = not posted. */
    postedAt: string | null;
    postedBy: string | null;
}

/** H38: one AI reader's line. */
export interface ReaderCheck {
    reader: "chatgpt" | "claude" | "perplexity";
    /** The crawler's name as robots.txt reads it: "GPTBot", "ClaudeBot", "PerplexityBot". */
    agent: string;
    /** robots.txt lets it in; null = robots.txt not read. */
    robotsAllows: boolean | null;
    /** A fetch as that reader got the page (not blocked, not a challenge page); null = not fetched. */
    gotIn: boolean | null;
    /** The page's words were in what it got (not an empty shell); null = not fetched. */
    wordsVisible: boolean | null;
    checkedAt: string | null;
}

/** H39: where approved copy goes. Oura cannot create a page on any site today, so a draft page is new backend work. */
/** H39 (D8): what `POST .../copy-to-site` makes: an unpublished draft page, or the approved copy as a file. */
export type CopyToSiteMode = "draft_page" | "download";

export interface CopyToSite {
    mode: CopyToSiteMode;
    /** A draft page is allowed on this site (a WordPress draft page). Wix and Framer: download only. */
    allowed: boolean;
    platform: SitePlatform | null;
    /** Why a draft page is not offered: "Oura cannot create pages on Framer". */
    reason?: string | null;
    /** The unpublished draft, once made; a person publishes it. */
    draftUrl?: string | null;
    /** The approved copy as a file. */
    downloadUrl?: string | null;
    docId?: string | null;
}

export type DeliveryGroup = "needs_us" | "waiting_client" | "checking" | "done" | "later";

export type ClientWord = "Done" | "In progress" | "Needs you" | "Not started";

/** Who acts next, and when. Never Oura doing work: Oura's rows are a re-check. */
export interface DeliveryActor {
    who: "person" | "client" | "oura_check";
    /** The account manager's name for "person". */
    name?: string | null;
    /** "today", "Fri 23 Oct", "3 days", "tonight". */
    when: string;
    late: boolean;
    cameBack: boolean;
}

export type MainActionKind =
    | "confirm_key_pages"
    | "mark_fixed"
    | "open_doc"
    | "link_doc"
    | "add_live_page"
    | "tick"
    | "check_now"
    | "close_by_hand"
    | "go_to"
    | "next_deliverable"
    /** v1.3 (H31): replaces H23's link_doc. "Create the doc", or with a docId "Read it, then share". */
    | "create_doc";

export interface MainAction {
    kind: MainActionKind;
    /** "Mark main headings fixed". */
    label: string;
    itemId?: string | null;
    docId?: string | null;
    /** For go_to: the staff view the work happens on ("reports", "visibility", "reviews"). */
    to?: string | null;
    /** For create_doc without a docId: the catalogue doc type to create ("Answer Plan"). */
    docType?: string | null;
    /** For create_doc: the codes the doc covers. */
    codes?: string[];
}

/**
 * H31 (v1.3): the deliverable's next step is its client document, due and
 * not shared. `create` when no doc covers the code; `share` with the covering
 * doc's id when one exists unshared, so Create is never offered twice. Read
 * by the main action `create_doc` (behind the Docs write switch).
 */
export interface DocStep {
    kind: "create" | "share";
    docId: string | null;
    docType: string;
    codes: string[];
    outlet?: string | null;
    /** YYYY-MM-DD: B5's `expectedBy` for the code. */
    expectedBy?: string | null;
}

export type HistoryArea = "scan" | "mark" | "reply" | "tick" | "page" | "carried" | "note" | "plan";

export interface HistoryEntry {
    at: string;
    text: string;
    area: HistoryArea;
    by?: string | null;
}

export interface DeliverableExtras {
    pagesSold?: number | null;
    pagesLive?: number | null;
    /** Impact only: it never closes a page. */
    aiNamedSinceLive?: { named: number; of: number } | null;
    movedOut?: { text: string; to: "visibility" | "document" | "note" }[];
    dismissedSuggestions?: { text: string; by: string; at: string }[];
    notes?: { text: string; by: string; at: string }[];
    /** B1: what lives in the document and never becomes an item. */
    inDocument?: string | null;
}

export interface DeliveryDocument {
    docId: string;
    title: string;
    /** The document's state in words: "Client replied 13 Oct", "Waiting on the client". */
    state: string;
    repliedAt?: string | null;
    /*
     * The wait facts (H13), from the Docs read. `null` means Docs has not read
     * the document's comments yet, never "not late".
     */
    stateKey?: "not_shared" | "not_due" | "needs_us" | "waiting_on_client" | "late" | "approved" | "nothing_asked" | null;
    waitingOn?: "client" | "us" | null;
    waitingSince?: string | null;
    /** Malaysian working days. */
    waitingWorkingDays?: number | null;
    late?: boolean | null;
    lateWorkingDays?: number | null;
    /** `YYYY-MM-DD`. */
    replyBy?: string | null;
    /** The document's link, for "Open the doc". */
    url?: string | null;
}

/** A document, or a code due through one, that needs someone to move: once per document (H13). */
export interface DocumentWait {
    /** `null` for a document not sent yet: `title` names the one the catalogue expects. */
    docId: string | null;
    title: string;
    codes: string[];
    outlet?: string | null;
    stateKey: "needs_us" | "waiting_on_client" | "late" | "not_sent_yet";
    waitingOn: "client" | "us" | null;
    waitingSince: string | null;
    waitingWorkingDays: number | null;
    late: boolean;
    lateWorkingDays: number | null;
    replyBy: string | null;
    expectedBy: string | null;
    /** The oldest question waiting on the client, its first line, up to 80 characters. */
    question: string | null;
    repliedAt: string | null;
    deliverableKeys: string[];
}

export interface Deliverable {
    /** `${code}-m${month}`, or `${code}-m${month}-${outlet}` for S2 when 2 or more outlets are sold (S4 keeps one deliverable, an item per outlet). */
    key: string;
    code: string;
    label: string;
    month: number;
    /** Null before the plan starts: there is no window to be in yet. */
    window: { start: string; end: string } | null;
    /** Foundation: the handover date. Growth: the month's report date.
     *  NULL on a plan that has not started (`phase: "not_started"`), which is
     *  what every deliverable of such a business carries. */
    due: string | null;
    group: DeliveryGroup;
    next: { itemId: string; text: string } | null;
    actor: DeliveryActor | null;
    /** The drawer head, by fixed template: "1 of 4 done · 1 waiting on scan · 2 to do". */
    head: string;
    clientWord: ClientWord;
    clientLine: string;
    items: DeliveryItem[];
    document?: DeliveryDocument | null;
    mainAction: MainAction;
    history: HistoryEntry[];
    /** Leaves the "N of M done" count. */
    reportedByHand: boolean;
    outlet?: string | null;
    extras?: DeliverableExtras | null;
    /** Done: when, and the proof line. */
    doneAt?: string | null;
    proofLine?: string | null;
    /** Reopened with a reason. */
    reopened?: { reason: string; by: string; at: string } | null;
    /** H31 (v1.3): the client document is the next step. null = it is not. */
    docStep?: DocStep | null;
    /**
     * Oura's check passed with no work logged since the plan started (Jack,
     * 22 Oct 2026): the site already passed. The deliverable waits in Needs us
     * until staff add what we did (`kind: "work"`) or mark it already fine,
     * and the client sees In progress until then. Absent when the server does
     * not send it: nothing changes.
     */
    passedWithoutWork?: { at: string } | null;
    /** Set when staff said it was already fine: it reads as checked, not as our work. */
    alreadyFine?: { by: string; at: string; note?: string | null } | null;
    /** Staff marked the whole deliverable Done by hand (#6463); Undo is a reopen. */
    forcedDone?: { by: string; at: string } | null;
    /**
     * Tasks from the client's Master Tracker sheet decide Done (backend #6486, frontend #4682):
     * `noTasks: false` = Done when all its tasks are Done in the sheet; `true` = no task carries
     * this code, so today's rules apply. Absent = the business does not use tasks: nothing changes.
     */
    noTasks?: boolean;
    taskCount?: number;
    tasksDone?: number;
    /** The latest Target among its tasks. */
    dueOn?: string | null;
    /** The sheet says Done, but Oura's check still fails. */
    checkDisagrees?: { at: string; said: string } | null;
}

/** What staff say about a deliverable that passed with no work logged. */
export type ProofIn = { kind: "work"; note: string; link?: string; file?: File | null } | { kind: "already_fine"; note?: string };

export interface KeyPage {
    url: string;
    source: "home" | "clicks" | "links" | "staff";
    clicks?: number | null;
    inlinks?: number | null;
    title?: string | null;
}

export interface KeyPages {
    pages: KeyPage[];
    /** Oura's suggestion, for the swap list. */
    suggested: KeyPage[];
    /** Other pages staff can swap in. */
    candidates: KeyPage[];
    confirmed: boolean;
    confirmedAt?: string | null;
    confirmedBy?: string | null;
}

/**
 * A suggestion from the last scan (H11): staff keep it (it becomes work under
 * its deliverable, H8 and H16) or dismiss it (Oura stops suggesting it).
 */
export interface DeliverySuggestion {
    id: string;
    /** "placeholder_pages", "press_page", "contradiction". */
    rule: string;
    evidenceKey?: string | null;
    action: string;
    /** Why, with the real numbers: the same sentence that reaches the client. */
    why: string;
    notes?: string | null;
    evidence?: { urls?: string[]; titles?: string[] } | null;
    state: "proposed" | "kept" | "dismissed";
    decidedBy?: string | null;
    decidedAt?: string | null;
}

export interface SuggestionsPayload {
    /** The latest usable scan the proposals come from. */
    assessmentId: string | null;
    proposals: DeliverySuggestion[];
    /** Every decision for this client, newest first. */
    decided: DeliverySuggestion[];
}

export interface LedgerCounts {
    total: number;
    items: number;
    carried: number;
    moved: number;
    outside: number;
    retired: number;
    unaccounted: number;
}

export interface LedgerRow {
    step: string;
    code?: string | null;
    /** "unaccounted": no destination yet, and the switch to Delivery is refused until there are none. */
    landsAs: "items" | "carried" | "moved" | "outside" | "retired" | "unaccounted";
    where: string;
}

export interface DeliveryPayload {
    businessId: string;
    businessName: string;
    asOf: string;
    phase: "foundation" | "growth" | "not_started";
    /** The current plan month; 1 is Foundation. */
    month: number;
    /** "Foundation, month 1" or "Month 2". */
    monthLabel: string;
    termMonths: number;
    /** The handover or the report date for the current month. Null before the
     *  plan starts. */
    due: string | null;
    /** "N of M done": the same number on Delivery, Overview, Home and Work. */
    doneCount: { done: number; total: number };
    cameBack: number;
    keyPages: { pages: KeyPage[]; confirmed: boolean; confirmedAt?: string | null; confirmedBy?: string | null };
    /** Every month of the term. */
    deliverables: Deliverable[];
    ledger: LedgerCounts;
    lastCheck: { at: string | null; kind: "scan" | "recheck" | null; running: boolean };
    accountManager?: string | null;
    /** The time of the next nightly re-check. */
    nextCheck?: string | null;
    /** Every document wait, once per document, late first (H13). */
    documentWaits?: DocumentWait[];
}

export interface TodayRow {
    businessId: string;
    businessName: string;
    deliverableKey: string;
    code: string;
    label: string;
    next: { itemId: string; text: string };
    actor: DeliveryActor;
    /** Sort position under the next-step rule. */
    rank: number;
    /** "today" (came back, a client reply, due by today) or "coming" (due later). */
    band: "today" | "coming";
}

export interface TodayPayload {
    asOf: string;
    rows: TodayRow[];
    /** Clients with no Delivery yet (no contract in Oura). */
    notSet: { businessId: string; businessName: string }[];
}

/**
 * The Friday note, drafted (H17): the server's ready draft and the facts it
 * was built from. Staff edit it in the box, then send it as any note.
 */
export interface NoteDraft {
    /** `YYYY-MM-DD`: the newer of 7 days ago and the last note sent. */
    since: string;
    until: string;
    draft: string;
    facts: {
        finished: { key: string; label: string; doneAt: string }[];
        /** Latest actions in the window, client copy already. */
        actions: { at: string; body: string }[];
        next: { key: string; label: string }[];
        waiting: { key: string; label: string; ask: string | null }[];
    };
    lastNote?: { date: string; author: string | null } | null;
}

export interface CheckNowResult {
    startedAt: string;
    etaSeconds: number;
}

/* ------------------------------------------------------------------------- *
 * Results. Every call resolves and never throws.
 * ------------------------------------------------------------------------- */

export type DeliveryFailureReason = "network" | "unauthorised" | "forbidden" | "notFound" | "notServed" | "rejected";

export interface DeliveryFailure {
    ok: false;
    reason: DeliveryFailureReason;
    status?: number | null;
    /** The server's own refusal code, when it sent one. */
    code?: string | null;
    message?: string | null;
}

export type DeliveryResult<T> = { ok: true; data: T } | DeliveryFailure;

export function deliveryFailure(reason: DeliveryFailureReason, extra: Partial<Omit<DeliveryFailure, "ok" | "reason">> = {}): DeliveryFailure {
    return { ok: false, reason, ...extra };
}

/* ------------------------------------------------------------------------- *
 * Bodies
 * ------------------------------------------------------------------------- */

export interface TickIn {
    link?: string;
    note?: string;
}

export interface PageIn {
    pageKind: "new" | "existing";
    url: string;
    /** What changed, for an existing page. */
    changed?: string;
}

export interface SlotIn {
    topic?: string;
    pageKind?: "new" | "existing";
}

/* ------------------------------------------------------------------------- *
 * H20a (v1.3): publish refusals, and the staff override with a reason
 * ------------------------------------------------------------------------- */

/** The publish routes' refusals (H20), slug in errors[0], 409. */
export type PublishRefusalCode = "APPROVAL_REQUIRED" | "draft_not_on_the_plan" | "wrong_item_for_draft";

/** What a refused publish carries in `data` (H20a adds the key and the doc, so the screen can name them). */
export interface PublishRefusal {
    code: PublishRefusalCode;
    /** The server's sentence: "The client has not approved this yet. It needs their yes in Copy Pack (B1)." */
    message: string;
    /** "S1:missing_meta", or a slot id; null for draft_not_on_the_plan. */
    approvalKey: string | null;
    /** The doc that still needs the client's yes; null = none shared. */
    docId: string | null;
    docTitle: string | null;
    /** A staff override can waive the client's yes for this key (APPROVAL_REQUIRED only). */
    canOverride: boolean;
}

/**
 * `PUT /businesses/{id}/task-approvals/{key}` with H20a's fields: an override
 * is set only by a named person, with a reason saying where the client said
 * yes. It shows in the approval record.
 */
export interface ApprovalOverrideIn {
    requirement: "required" | "not_required";
    showToClient?: boolean;
    /** The named person setting it (the server checks it against the token). */
    name: string;
    /** Why: "Siew Ling agreed the menu copy on WhatsApp, 1 Oct". */
    reason: string;
    /** Where the client said yes: "WhatsApp, 1 Oct, 3.12pm". */
    whereClientSaidYes: string;
}

export interface ApprovalOverrideRecord {
    key: string;
    requirement: "required" | "not_required";
    source: "staff";
    setBy: string;
    setAt: string;
    reason: string;
    whereClientSaidYes: string;
}
